import { Data } from '@blade/std-lib'

import type { ThemeId } from './theme-id'
import type { Appearance, Theme as DelugeTheme } from '@blade/ui-theme'

export class CustomTheme extends Data.TaggedClass('CustomTheme')<{
  baseTheme: ThemeId
  customColors: CustomThemeColors
  appearance: Appearance
  appearanceColor: string
}> {}

export type ThemeColor = string // hex #123456

export interface CustomThemeColors {
  // base
  primary?: ThemeColor
  background?: ThemeColor
  text?: ThemeColor

  // trend
  up?: ThemeColor
  upAccent?: ThemeColor
  down?: ThemeColor
  downAccent?: ThemeColor
  neutral?: ThemeColor
  neutralAccent?: ThemeColor

  // candle
  candleGreen?: ThemeColor
  candleGreenWick?: ThemeColor
  candleGreenBorder?: ThemeColor
  candleRed?: ThemeColor
  candleRedWick?: ThemeColor
  candleRedBorder?: ThemeColor

  // intents
  warning?: ThemeColor
  warningAccent?: ThemeColor
  success?: ThemeColor
  successAccent?: ThemeColor
  error?: ThemeColor
  errorAccent?: ThemeColor

  // colors
  gray?: ThemeColor
  blue?: ThemeColor
  red?: ThemeColor
  green?: ThemeColor
  orange?: ThemeColor
  amber?: ThemeColor
  acid?: ThemeColor
  cyan?: ThemeColor
  purple?: ThemeColor
  pink?: ThemeColor
  yellow?: ThemeColor
  silver?: ThemeColor
  bronze?: ThemeColor
  golden?: ThemeColor

  // subscription
  subscriptionGradientFrom?: ThemeColor
  subscriptionGradientTo?: ThemeColor
  subscriptionIcon?: ThemeColor
  subscriptionGlow?: ThemeColor
  subscriptionButton?: ThemeColor

  // boost
  boostGradientFrom?: ThemeColor
  boostGradientTo?: ThemeColor
  boostIcon?: ThemeColor
  boostGlow?: ThemeColor
  boostButton?: ThemeColor
}

export const patchCustomThemeColors = (
  themeColors: CustomThemeColors,
  patch: Partial<CustomThemeColors>,
) => {
  return {
    ...themeColors,
    ...patch,
  }
}

const CUSTOM_THEME_COLOR_KEYS = [
  'primary',
  'background',
  'text',
  'up',
  'upAccent',
  'down',
  'downAccent',
  'neutral',
  'neutralAccent',
  'candleGreen',
  'candleGreenWick',
  'candleGreenBorder',
  'candleRed',
  'candleRedWick',
  'candleRedBorder',
  'warning',
  'warningAccent',
  'success',
  'successAccent',
  'error',
  'errorAccent',
  'gray',
  'blue',
  'red',
  'green',
  'orange',
  'amber',
  'acid',
  'cyan',
  'purple',
  'pink',
  'yellow',
  'silver',
  'bronze',
  'golden',
  'subscriptionGradientFrom',
  'subscriptionGradientTo',
  'subscriptionIcon',
  'subscriptionGlow',
  'subscriptionButton',
  'boostGradientFrom',
  'boostGradientTo',
  'boostIcon',
  'boostGlow',
  'boostButton',
] as const satisfies ReadonlyArray<keyof CustomThemeColors>

export const customThemeColorsFromBaseTheme = (
  baseTheme: DelugeTheme,
): CustomThemeColors => {
  const { palette } = baseTheme

  return Object.fromEntries(
    CUSTOM_THEME_COLOR_KEYS.map((key) => [key, palette[key][9]]),
  ) as CustomThemeColors
}
